import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { TeamSpendOut } from '../types';
import { SpendChart } from './SpendChart';

interface TeamSpendViewProps {
  teamId: string;
  teamName: string;
}

/**
 * Per-team spend chart with loading state. Aggregates all keys in the team's workspace.
 * Placed in a team detail view or admin team panel.
 */
export function TeamSpendView({ teamId, teamName }: TeamSpendViewProps) {
  const [data, setData] = useState<TeamSpendOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const generation = useRef(0);

  const load = useCallback(
    async (newDays: number) => {
      const gen = ++generation.current;
      const current = () => gen === generation.current;
      setLoading(true);
      setError(null);
      try {
        const result = await api.teamSpend(teamId, newDays);
        if (!current()) return;
        setData(result);
      } catch (err) {
        if (!current()) return;
        setError(err instanceof Error ? err.message : String(err));
        setData(null);
      } finally {
        if (current()) setLoading(false);
      }
    },
    [teamId]
  );

  useEffect(() => {
    void load(days);
    return () => {
      generation.current++;
    };
  }, [load, days]);

  const handleDaysChange = (newDays: number) => {
    setDays(newDays);
  };

  if (loading) {
    return <div className="muted small">Loading team spend history...</div>;
  }

  if (error) {
    return <div className="callout callout-warning">Failed to load team spend history: {error}</div>;
  }

  if (!data) {
    return <div className="muted small">No team spend data available</div>;
  }

  return (
    <div>
      <h3>{teamName} spend history</h3>
      <SpendChart
        points={data.points}
        asOf={data.as_of}
        days={days}
        onDaysChange={handleDaysChange}
      />
    </div>
  );
}
